import { alpha, createTheme } from "@mui/material/styles";
import type { Theme } from "@mui/material/styles";

type ThemeMode = "light" | "dark";

const paletteTokens = {
  light: {
    primary: "#3367d6",
    primaryLight: "#6f95e7",
    primaryDark: "#244da7",
    secondary: "#4ba7a7",
    backgroundDefault: "#f7f9fd",
    backgroundPaper: "#ffffff",
    textPrimary: "#1f2940",
    textSecondary: "#5f6f8f",
  },
  dark: {
    primary: "#7ea2ff",
    primaryLight: "#abc3ff",
    primaryDark: "#5379d6",
    secondary: "#6cc2c2",
    backgroundDefault: "#0f1726",
    backgroundPaper: "#162033",
    textPrimary: "#f5f7fb",
    textSecondary: "#afbdd4",
  },
} as const;

export const getPrimaryGradient = (theme: Theme) =>
  `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`;

export const getAppShellBackground = (theme: Theme) =>
  theme.palette.mode === "dark"
    ? "linear-gradient(180deg, #0f1726 0%, #101a2d 35%, #0d1523 100%)"
    : "linear-gradient(180deg, #f7f9fd 0%, #eff4fb 40%, #f8fbff 100%)";

export const getHeaderBarGradient = (theme: Theme) =>
  theme.palette.mode === "dark"
    ? "linear-gradient(90deg, #10192b 0%, #17233a 52%, #111a2d 100%)"
    : "linear-gradient(90deg, #1f2d45 0%, #243650 50%, #21314b 100%)";

export const getBrandMarkGradient = (theme: Theme) =>
  `linear-gradient(145deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`;

export const getElevatedSurfaceGradient = (theme: Theme) =>
  theme.palette.mode === "dark"
    ? `linear-gradient(145deg, ${alpha(theme.palette.background.paper, 0.98)} 0%, ${alpha(
        theme.palette.background.default,
        0.98,
      )} 100%)`
    : `linear-gradient(145deg, ${alpha(theme.palette.background.paper, 0.96)} 0%, ${alpha(
        theme.palette.primary.contrastText,
        0.98,
      )} 100%)`;

export const getBrandGlow = (theme: Theme) =>
  theme.palette.mode === "dark"
    ? `0 10px 26px ${alpha(theme.palette.background.default, 0.45)}`
    : `0 10px 24px ${alpha(theme.palette.primary.main, 0.22)}`;

export const getContainedButtonDisabledStyles = (theme: Theme) => ({
  color: alpha(theme.palette.text.primary, theme.palette.mode === "dark" ? 0.72 : 0.56),
  background: `linear-gradient(90deg, ${alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.34 : 0.2)} 0%, ${alpha(
    theme.palette.secondary.main,
    theme.palette.mode === "dark" ? 0.36 : 0.24,
  )} 100%)`,
  border: `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.28 : 0.16)}`,
});

export const getOverlayBackdropColor = (theme: Theme) =>
  theme.palette.mode === "dark" ? alpha(theme.palette.background.default, 0.38) : alpha(theme.palette.background.default, 0.34);

export const createReconTheme = (mode: ThemeMode) => {
  const tokens = paletteTokens[mode];

  return createTheme({
    shape: {
      borderRadius: 12,
    },
    palette: {
      mode,
      primary: {
        main: tokens.primary,
        light: tokens.primaryLight,
        dark: tokens.primaryDark,
        contrastText: mode === "dark" ? "#08111f" : "#ffffff",
      },
      secondary: {
        main: tokens.secondary,
        contrastText: mode === "dark" ? "#08111f" : "#ffffff",
      },
      background: {
        default: tokens.backgroundDefault,
        paper: tokens.backgroundPaper,
      },
      text: {
        primary: tokens.textPrimary,
        secondary: tokens.textSecondary,
      },
      divider: alpha(tokens.textPrimary, mode === "dark" ? 0.16 : 0.12),
    },
    typography: {
      fontFamily: '"RockSansNormal", "Roboto", sans-serif',
      h4: {
        fontWeight: 700,
        letterSpacing: "-0.03em",
      },
      button: {
        fontWeight: 600,
        textTransform: "none",
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          "::-webkit-scrollbar": {
            width: 6,
            height: 6,
          },
          "::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "::-webkit-scrollbar-thumb": {
            borderRadius: 3,
          },
          "*": {
            scrollbarWidth: "thin",
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paperAnchorLeft: {
            marginTop: 0,
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            borderRadius: 999,
            minHeight: 42,
            "&.MuiButton-outlined.Mui-disabled": {
              opacity: 0.72,
            },
            "&.MuiButton-contained.Mui-disabled": {
              opacity: 1,
            },
          },
          containedPrimary: ({ theme }) => ({
            background: getPrimaryGradient(theme),
            color: mode === "dark" ? "#ffffff" : theme.palette.primary.contrastText,
            "&.Mui-disabled": getContainedButtonDisabledStyles(theme),
          }),
        },
      },
      MuiToggleButtonGroup: {
        styleOverrides: {
          grouped: {
            margin: 0,
            "&:not(:first-of-type)": {
              borderRadius: 999,
            },
            "&:first-of-type": {
              borderRadius: 999,
            },
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            minHeight: 44,
            paddingInline: theme.spacing(2.25),
            borderRadius: 999,
            border: `1px solid ${alpha(theme.palette.text.primary, theme.palette.mode === "dark" ? 0.18 : 0.12)}`,
            color: theme.palette.text.secondary,
            backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === "dark" ? 0.42 : 0.68),
            fontWeight: 700,
            "&:hover": {
              backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.14 : 0.08),
            },
            "&.Mui-selected, &.Mui-selected:hover": {
              color: theme.palette.mode === "dark" ? "#ffffff" : theme.palette.primary.contrastText,
              background: getPrimaryGradient(theme),
              borderColor: alpha(theme.palette.primary.main, 0.44),
              boxShadow: `0 8px 18px ${alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.24 : 0.18)}`,
            },
          }),
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            display: "flex",
            alignContent: "center",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "row",
            flexWrap: "nowrap",
          },
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            boxShadow: "none",
            border: `1px solid ${alpha(tokens.textPrimary, mode === "dark" ? 0.1 : 0.08)}`,
            backgroundColor: tokens.backgroundPaper,
            "&::before": {
              display: "none",
            },
          },
        },
      },
    },
  });
};
